import { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile, updatePassword as fbUpdatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { getMyRestaurantId } from '../utils/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState(null);
  const [error, setError] = useState(null);

  // Handle Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log('[AuthContext] Auth state changed:', firebaseUser?.email);
      setUser(firebaseUser);
      if (!firebaseUser) {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch and store restaurantId after login
  useEffect(() => {
    if (user) {
      setLoading(true);
      setError(null);
      console.log('[AuthContext] Fetching restaurant ID for user:', user.email);
      
      getMyRestaurantId()
        .then(data => {
          if (data.restaurantId) {
            console.log('[AuthContext] Restaurant ID found:', data.restaurantId);
            setRestaurantId(data.restaurantId);
          } else {
            console.log('[AuthContext] No restaurant found for user');
            setError('No restaurant found for this user');
            setRestaurantId(null);
          }
        })
        .catch(err => {
          console.error('[AuthContext] Error fetching restaurant:', err);
          setError(err.message || 'Failed to fetch restaurant information');
          setRestaurantId(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRestaurantId(null);
      setError(null);
      setLoading(false);
    }
  }, [user]);

  const login = async (email, password) => {
    try {
      setError(null);
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (email, password, name) => {
    try {
      setError(null);
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (name) {
        await updateProfile(cred.user, { displayName: name });
      }
      return cred;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setRestaurantId(null);
      setError(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updatePassword = async (currentPassword, newPassword) => {
    if (!auth.currentUser || !auth.currentUser.email) throw new Error('No user logged in');
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    // Update password
    await fbUpdatePassword(auth.currentUser, newPassword);
  };

  const sendResetEmail = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error,
      login, 
      register, 
      logout, 
      restaurantId,
      updatePassword,
      sendResetEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext; 